import React, { useState, useCallback, useEffect } from "react";
import "./organization-access-code-create-edit.scss";

import { GSForm, GSSidePanelPage } from "golfstatus_react_components";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMatch } from "react-router-dom";
import { selectCurrentOrganization } from "../../reducers/organizationSlice";

import { accessCodeForm } from "../../forms/AccessListForm";

import {
  selectAccessListNotifications,
  selectCurrentList,
  selectCurrentAccessCode,
  selectCurrentAccessCodeMember,
  getOrganizationAccessList,
  getOrganizationAccessListCode,
  getOrganizationAccessListCodeMember,
  saveAccessCode,
  createAccessCode,
  clearNotifications,
  setSelectedCode,
} from "../../reducers/accessSlice";

const OrganizationAccessCodeCreateEdit = () => {
  const [isValid, setIsValid] = useState(false);

  const [code, setCode] = useState({});
  const [member, setMember] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const matchEdit = useMatch("/:id/access/:accessListID/code/:accessCodeID/*");

  const orgNotifications = useSelector(selectAccessListNotifications);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const currentList = useSelector(selectCurrentList);
  const currentCode = useSelector(selectCurrentAccessCode);
  const currentCodeMember = useSelector(selectCurrentAccessCodeMember);


  const loadCode = useCallback(() => {
    dispatch(getOrganizationAccessList(matchEdit?.params?.accessListID));
    if (matchEdit?.params?.accessCodeID !== "add") {
      dispatch(getOrganizationAccessListCode(matchEdit?.params?.accessCodeID));
      dispatch(getOrganizationAccessListCodeMember(matchEdit?.params?.accessCodeID));
    } else {
      setCode({});
    }
  }, [dispatch, matchEdit?.params]);

  useEffect(() => {
    loadCode();
    return () => {
      dispatch(setSelectedCode({}));
    };
  }, [loadCode, dispatch]);

  useEffect(() => {
    const { id, accessCodeID } = matchEdit?.params;
    if (currentCode?.id) {
      if (currentCode?.id !== accessCodeID) {
        navigate(`/${id}/access/${currentList.id}/code/${currentCode.id}`);
      } else {
        let accessCode = {...currentCode}
        if(accessCode?.maxUseQuantity === null)
        {
          accessCode.codeUsage = { id: 1, label: "Unlimited Use", value: 1 }
        }
        else{
          accessCode.codeUsage = { id: 2, label: "Limited Use", value: 2 }
        }
        setCode(accessCode);
      }
    } else {
      setCode({identifier: "", maxUseQuantity: "", status: "active", activateOn:"", deactivateOn: "", codeUsage: { id: 1, label: "Unlimited Use", value: 1 }});
      setMember({firstName: "", lastName:"", email:"", handicap: ""});
    }
  }, [currentCode, currentList, matchEdit?.params, navigate]);

  useEffect(() => {
    if (currentCodeMember?.id) {
      setMember(currentCodeMember);
    } else {
      setMember({});
    }
  }, [currentCodeMember, matchEdit?.params, navigate]);

  //form context
  let context = {};

  context.validationFailed = () => {
    if (isValid) {
      setIsValid(false);
    }
  };

  context.updateCode = (value, property) => {
    setIsValid(true);
    let access = { ...code };
    access[property] = value;
    setCode(access);
  };

  context.updateMember = (value, property) => {
    setIsValid(true);
    let user = { ...member };
    user[property] = value;
    setMember(user);
  };

  context.isEditable = () => {
    return matchEdit?.params?.accessCodeID === "add";
  };

  context.generateCode = () => {
    let newCode = Math.random()
      .toString(36)
      .substring(2, 10)
      .toUpperCase();
    context.updateCode(newCode, "identifier");
  };

  const saveCode = () => {
    setIsValid(false);
    let save = { ...code };
    if(save.activateOn)
    {
      save.activateOn = new Date(save.activateOn).toISOString()
    }
    if(save.deactivateOn)
    {
      save.deactivateOn = new Date(save.deactivateOn).toISOString()
    }
    if(code.codeUsage.id === 1)
    {
      save.maxUseQuantity = null;
    }
    save.accessMember = member
    save.accessGroup = currentList
    dispatch(saveAccessCode(save));
  };

  const addCode = () => {
    setIsValid(false);
    let create = { ...code };
    if(create.activateOn)
    {
      create.activateOn = new Date(create.activateOn).toISOString()
    }
    if(create.deactivateOn)
    {
      create.deactivateOn = new Date(create.deactivateOn).toISOString()
    }
    create.accessMember = member;
    create.accessGroup = currentList;
    create.organization = currentOrganization;
    dispatch(createAccessCode(create));
  };

  const deleteCode = () => {
    const { id, accessListID, accessCodeID } = matchEdit.params;
    navigate(`/${id}/access/${accessListID}/code/${accessCodeID}/delete`);
  };

  const leftNavigation = () => {
    dispatch(clearNotifications());
    if(matchEdit?.params?.["*"] === "add")
    {
      const { id } = matchEdit?.params;
      navigate(`/${id}/access`)
    }
    else{
      const { id, accessListID} = matchEdit?.params;
      navigate(`/${id}/access/${accessListID}`)
    }
    //navigate(-1);
  };

  const getActions = () => {
    let actions = [{ name: "cancel", action: leftNavigation, type: "grey" }];

    if (context?.isEditable?.()) {
      actions.unshift?.({
        name: "Add",
        isDisabled: !isValid,
        action: addCode,
        type: "black",
      });
    } else {
      actions?.unshift?.({
        name: "Save",
        isDisabled: !isValid,
        action: saveCode,
        type: "black",
      });
      actions?.push?.({
        name: "Delete Code",
        action: deleteCode,
        type: "transparent red",
      });
    }
    return actions;
  };

  const getNavigation = () => {
    return {
      title: "Code Details",
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const hasNotifications = (notifications) => {
    return notifications && notifications.length > 0;
  };

  const getBanner = () => {
    const slideOutNotifications = orgNotifications;
    if (hasNotifications(slideOutNotifications)) {
      return {
        notifications: slideOutNotifications.map((n) => ({
          ...n,
          pageActions: getBannerActions(),
        })),
        timeoutAction: timeoutAction,
        timeout: 10000,
      };
    }
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const getBannerActions = () => {
    return [
      {
        id: 1,
        title: "View Access List Overview",
        size: "secondary",
        actionClick: leftNavigation,
      },
    ];
  };

  const getActiveAction = () => {
    return [
      {
        actionType: "toggle",
        value: true,
        pageActionProps: {
          falseDescription: "Inactive",
          rowReverse: true,
          onClick: (e) =>
            context.updateCode(
              code?.status === "active" ? "inactive" : "active",
              "status"
            ),
          trueDescription: "Active",
          value: code?.status === "active",
        },
      },
    ];
  };

  const getFormActionBar = () => {
    return {
      formTitle: context.isEditable()
        ? "Add Code"
        : "Edit Code",
      formActions: getActiveAction(),
    };
  };

  const getContent = () => {
    return (
      <GSForm
        {...getFormActionBar()}
        formSections={accessCodeForm(context, code, member)}
      />
    );
  };
  const getDrawer = () => {
    return {
      actions: getActions(),
    };
  };
  return (
    <organization-access-code-create-edit>
      <GSSidePanelPage
        header={getNavigation()}
        banner={getBanner()}
        content={getContent()}
        drawer={getDrawer()}
      />
    </organization-access-code-create-edit>
  );
};

export default OrganizationAccessCodeCreateEdit;
